import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Table, TableContainer, Tbody, Td, Th, Thead, Tr } from 'components/v2/Table'
import { PhisicalSimsCSVModal } from 'features/BusinessClients/components/PhisicalSimsCSVModal'
import { IPhysicalSimField } from 'features/BusinessClients/types'
import { useField } from 'formik'
import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { DownloadIcon } from 'components/Icons/DownloadIcon'
import { Tag } from 'components/v2/Tag'
import styled from 'styled-components'
import { Row } from 'components/v2/Row'
import { CsvFileValuesDropdown } from 'features/BusinessClients/components/CsvFileValuesDropdown'

const StyledTag = styled(Tag)`
  background-color: #f4f0ff;
  border-radius: 8px;
  border: none;
  padding: 8px 16px;
`
const DownloadButton = styled.button`
  text-decoration: none;
  color: #916ee8;
  background-color: transparent;
  border: none;
  cursor: pointer;
`
const Flex = styled.div`
  justify-content: center;
  align-items: center;
  display: flex;
  gap: 8px;
  color: #916ee8;
  > svg {
    fill: #916ee8;
  }
`

export const PhysicalSimsFormField = ({ fieldName }: { fieldName: string }): ReactElement => {
  const [field, , helpers] = useField<IPhysicalSimField[]>(fieldName)
  const [isOpenModal, setIsOpenModal] = useState(false)
  const isShowingTable = field.value.length > 0

  const onSubmit = async ({ filePath, file }: { filePath: string; file: File }): Promise<void> => {
    helpers.setValue([
      ...field.value,
      {
        date: new Date(),
        file: file,
        filePath: filePath
      }
    ])
  }

  const downloadFile = (file: File): void => {
    if (file) {
      const url = URL.createObjectURL(file)
      const a = document.createElement('a')
      a.href = url
      a.download = file.name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  const isShowingFileColumn = false //TODO: Will be implemented in a future Jira ticket
  const isShowingQuantityColumn = false //TODO: Will be implemented in a future Jira ticket

  return (
    <>
      <FormLayout.Card>
        <FormLayout.CardHeader>
          <Heading.H4>
            <FormattedMessage id="newBusinessClientForm.physicalSims.title" defaultMessage="Physical SIMs" />
          </Heading.H4>
          <Button kind="secondary" type="button" onClick={(): void => setIsOpenModal(true)}>
            <FormattedMessage id="newBusinessClientForm.addPhysicalSimCsv" defaultMessage="Upload CSV" />
          </Button>
        </FormLayout.CardHeader>
        {isShowingTable ? (
          <Row>
            <Column>
              <TableContainer>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>
                        <FormattedMessage id="newBusinessClientForm.physicalSims.addedBy" defaultMessage="Added By" />
                      </Th>
                      <Th>
                        <FormattedMessage id="newBusinessClientForm.physicalSims.date" defaultMessage="Date" />
                      </Th>
                      {isShowingQuantityColumn && (
                        <Th>
                          <FormattedMessage
                            id="newBusinessClientForm.physicalSims.quantity"
                            defaultMessage="Quantity"
                          />
                        </Th>
                      )}
                      {isShowingFileColumn && (
                        <Th>
                          <FormattedMessage id="newBusinessClientForm.physicalSims.file" defaultMessage="File" />
                        </Th>
                      )}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {field.value.map((sim, index) => {
                      return (
                        <Tr key={index}>
                          <Td>
                            {sim.id ? (
                              sim.addedBy
                            ) : (
                              <FormattedMessage id="dictionary.pending" defaultMessage="Pending" />
                            )}
                          </Td>
                          <Td>{sim.date.toLocaleDateString()}</Td>
                          {isShowingQuantityColumn && (
                            <Td>{Number(sim.quantity) > 0 ? <CsvFileValuesDropdown data={[]} /> : 0}</Td>
                          )}
                          {isShowingFileColumn && (
                            <Td>
                              <DownloadButton onClick={(): void => downloadFile(sim.file as File)}>
                                <StyledTag>
                                  <Flex>
                                    <span>CSV</span>
                                    <DownloadIcon />
                                  </Flex>
                                </StyledTag>
                              </DownloadButton>
                            </Td>
                          )}
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </Column>
          </Row>
        ) : null}
      </FormLayout.Card>
      {isOpenModal && <PhisicalSimsCSVModal setIsOpen={setIsOpenModal} onSubmit={onSubmit} />}
    </>
  )
}
